// v2.71.6 Phase 6 — Saved Searches bar
// Renders a horizontal chip row of the user's saved property searches plus a
// "Save this search" CTA. Anonymous visitors see a soft sign-in prompt only.
//
// Props:
//   filters     — current Dashboard filter state
//   setFilters  — Dashboard filter setter (used to apply a saved chip)
//   defaults    — defaultFilters() — used to detect "All listings" empty state
//   onRequireLogin — () => void — invoked when an anon user clicks Save

import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { describeFilters } from "../lib/filterUrl";
import { Bookmark, Plus, X, Search } from "lucide-react";
import { toast } from "sonner";

export default function SavedSearchesBar({ filters, setFilters, defaults, onRequireLogin }) {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user) { setItems([]); return; }
    try {
      const r = await api.get("/saved-searches");
      setItems(r.data?.results || []);
    } catch { setItems([]); }
  }, [user]);

  useEffect(() => { if (!authLoading) load(); }, [authLoading, load]);

  const onSave = async () => {
    if (!user) { onRequireLogin?.(); return; }
    if (busy) return;
    // Default name = a short summary of the filters
    const fallback = describeFilters(filters, defaults);
    const name = window.prompt("Name this search:", fallback);
    if (!name) return;
    setBusy(true);
    try {
      const r = await api.post("/saved-searches", { name, filters });
      setItems((arr) => [r.data, ...arr]);
      toast.success("Search saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save");
    }
    setBusy(false);
  };

  const onApply = (s) => {
    setFilters({ ...defaults, ...(s.filters || {}) });
  };

  const onDelete = async (s, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/saved-searches/${s.id}`);
      setItems((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Deleted");
    } catch {
      toast.error("Couldn't delete");
    }
  };

  // Hide entirely if no user AND no chips (anon empty state is too noisy)
  if (!user && items.length === 0) {
    // Render a single compact CTA so the feature is still discoverable
    return (
      <div className="hidden md:flex items-center gap-2 mb-4" data-testid="saved-searches-bar-anon">
        <button
          type="button"
          onClick={onSave}
          className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold text-[#52525B] hover:text-[#0055FF] border border-dashed border-[#E4E4E7] hover:border-[#0055FF] rounded-full px-3 py-1.5 transition-colors"
          data-testid="saved-search-anon-cta"
        >
          <Bookmark size={12} /> Sign in to save searches
        </button>
      </div>
    );
  }

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="saved-searches-bar">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#A1A1AA] inline-flex items-center gap-1">
        <Search size={11} /> Saved
      </span>
      {items.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onApply(s)}
          className="group inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#09090B] bg-white border border-[#E4E4E7] hover:border-[#0055FF] hover:text-[#0055FF] rounded-full px-3 py-1.5 transition-colors max-w-[260px]"
          data-testid={`saved-search-chip-${s.id}`}
          title={s.name}
        >
          <span className="truncate">{s.name}</span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => onDelete(s, e)}
            onKeyDown={(e) => { if (e.key === "Enter") onDelete(s, e); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity w-4 h-4 inline-flex items-center justify-center rounded-full hover:bg-[#FEE2E2] hover:text-[#DC2626]"
            data-testid={`saved-search-del-${s.id}`}
            aria-label="Delete saved search"
          >
            <X size={10} />
          </span>
        </button>
      ))}
      <button
        type="button"
        onClick={onSave}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#0055FF] hover:text-white hover:bg-[#0055FF] border border-[#0055FF]/30 hover:border-[#0055FF] rounded-full px-3 py-1.5 transition-colors disabled:opacity-50"
        data-testid="saved-search-save-btn"
      >
        <Plus size={12} /> Save this search
      </button>
    </div>
  );
}
